<template>
  <div>
    <Subheader v-model="search" title="Cofactors" search="cofactors">
      <v-tooltip bottom>
        <template :disabled="canAddCofactors" v-slot:activator="{ on }">
          <span v-on="on">
            <v-btn color="success" @click="addCofactor()" :disabled="!canAddCofactors">
              <v-icon class="mr-1">add</v-icon>{{ $t('Button.AddCoFactorAnalyteLink') }}
            </v-btn>
          </span>
        </template>
        <span>All cofactors have been added.</span>
      </v-tooltip>
    </Subheader>
    <v-card class="mb-3">
      <v-card-text>
        <v-data-table :headers="headers" :items="cofactorAnalyteList" :search="search" hide-actions>
          <template slot="items" slot-scope="props">
            <tr :class="props.item.IsMismatched ? 'warning lighten-2' : ''" 
            :title="props.item.IsMismatched ? $t('GuidelineCoFactor.CoFactorAnalyteMismatchedUnitGroupMsg', 
            {analyte: props.item.Mismatched.AnalyteName, analyteUnitGroup: props.item.Mismatched.AnalyteUnitGroup, 
            cofactor: props.item.Mismatched.Name, cofactorUnitGroup: props.item.Mismatched.CofactorUnitGroup}) : null">
            <td class="text-xs-left">{{ props.item.CoFactorName }}</td>
            <td class="text-xs-left">{{ props.item.AnalyteName }}</td>
            <td class="text-xs-left">{{ props.item.Description }}</td>
            <td class="text-xs-center">
              <v-btn small dark color="warning" @click="clickEdit(props.item)">
                <v-icon class="mr-1">edit</v-icon>{{ $t('Button.Edit') }}
              </v-btn>
              <v-btn small dark color="delete" @click="clickDelete(props.item)">
                <v-icon class="mr-1">delete_forever</v-icon>{{ $t('Button.Delete') }}
              </v-btn>              
            </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    
    <Subheader title="Cofactor Calculation Strategy" />
    <v-card>
      <v-card-text>
        <v-layout wrap align-center>
          <div style="flex: 0 0 125px;" class="font-weight-bold">Current Strategy:</div>
          <div>{{ currentAlgorithmName }}</div>
          <div class="text-xs-right">
            <v-btn small @click="formModal = true">
              <v-icon color="warning" class="mr-1">edit</v-icon>{{ $t('Button.Edit') }}
            </v-btn>
          </div>
        </v-layout>
        <v-layout>
          <div style="flex: 0 0 125px;" class="font-weight-bold">Description:</div>
          <div>{{ selectedAlgorithmDescription }}</div>
        </v-layout>
      </v-card-text>
      <v-divider />
      <v-flex xs12 pb-4 v-if="!algorithmIsModified && !noDefaultValue">
        <v-progress-linear v-if="isLoading" indeterminate />
        <v-window v-else v-model="configWindowKey">
          <v-window-item v-for="algo in algorithms" :key="algo.Key" :value="algo.Key">
            <component v-bind:is="algo.ConfigComponent" @notify="handleNotification" :mapSameAnalyte="mapSameAnalyte"></component>
          </v-window-item>
        </v-window>
      </v-flex>
    </v-card>

    <AppModal v-model="addDialog" persistent max-width="800">
      <template v-slot:title>{{$t('GuidelineCoFactor.addTitle')}}</template>
      <template v-slot:text>
        <v-layout wrap>
          <v-form v-model="forms.addCofactorFormValid">
            <v-flex xs12 pl-3 pr-3>
              <v-autocomplete :rules="[rules.required]" label="CoFactor" v-bind:items="addCoFactorList" v-model="cofactorData.cofactorId" item-text="Name" item-value="Id"></v-autocomplete>
            </v-flex>
            <v-flex xs12 pl-3 pr-3>
              <v-autocomplete label="Link Analyte" v-bind:items="analyteListMatchCofactorUnit" v-model="cofactorData.AnalyteId" item-text="AnalyteName" item-value="Id"></v-autocomplete>
            </v-flex>
            <v-alert v-show="!isCofactorAnalyteMapValid" type="warning" outline>
              <div>{{ confirmMessage }}</div>
            </v-alert>  
          </v-form>
        </v-layout>
      </template>
      <template v-slot:actions>
        <v-spacer />
        <v-btn color="success" :disabled="!forms.addCofactorFormValid" @click="saveCofactor()">
          <v-icon>done</v-icon>{{ $t('Button.Save') }}
        </v-btn>
        <v-btn color="secondary" @click="closeDialog()" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </AppModal>

    <AppModal v-model="editDialog" persistent max-width="800">
      <template v-slot:title>{{$t('GuidelineCoFactor.editTitle')}}</template>
      <template v-slot:text>
        <v-form v-model="forms.editCofactorFormValid">
          <v-autocomplete label="CoFactor" v-bind:hint="'Cofactor is used in unit group(s):' + getHint(cofactorData.cofactorId)" persistent-hint v-bind:items="cofactorList" v-model="cofactorData.cofactorId" item-text="Name" item-value="Id" disabled></v-autocomplete>
          <v-autocomplete label="Link Analyte" v-bind:items="analyteListMatchCofactorUnit" v-model="cofactorData.AnalyteId" item-text="AnalyteName" item-value="Id"></v-autocomplete>
          <v-alert v-show="!isCofactorAnalyteMapValid" type="warning" outline>
            <div>{{ confirmMessage }}</div>
          </v-alert>
        </v-form>
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn :disabled="!forms.editCofactorFormValid" color="success" @click="clickEditSave()">
          <v-icon>done</v-icon>{{ $t('Button.Save') }}
        </v-btn>
        <v-btn color="secondary" @click="closeDialog()" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </AppModal>

    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-toolbar dense color="primary" dark style="padding: 0px;">
          <v-toolbar-title>{{$t('GuidelineCoFactor.deleteTitle')}}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="deleteDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>{{$t('GuidelineCoFactor.deleteMsg')}}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="agreeDelete()">
            <v-icon>delete_forever</v-icon>{{ $t('Button.Delete') }}
          </v-btn>
          <v-btn color="secondary" @click="deleteDialog = false" outline>{{ $t('Button.Cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <FormModal v-model="selectedAlgorithm" :original-value="currentAlgorithm" :dialog="formModal" @change="updateFormModal" @save="clickSaveAlgorithmStrategy" @cancel="resetFormValues" @notify="handleNotification"></FormModal>
    <v-snackbar style="text-align:center" :color="snackColor" :timeout="null" top v-model="snackbar">
      {{ snackMessage }}
      <v-btn dark flat @click.native.stop.prevent="snackbar = false">{{ $t('Button.Close') }}</v-btn>
    </v-snackbar>
    <ErrorDialog v-model="errorDialog.show" :data="errorDialog.data" :title="errorDialog.title"></ErrorDialog>
  </div>
</template>

<script>
  import GuidelinesDetailDefaultValues from '@/components/CoFactorManage/GuidelineAnalyteDefaultValues'
  import OverrideStrategyConfig from '@/components/CoFactorManage/CoFactor/CoFactorConfig/OverrideStrategyConfig'
  import StationDataWithFallbackStrategyConfig from '@/components/CoFactorManage/CoFactor/CoFactorConfig/StationDataWithFallbackStrategyConfig'
  import FormModal from '@/components/CoFactorManage/CoFactor/Form/FormModal'
  import {
    algorithms,
  } from '@/helpers/algorithms'
  import {
    createNamespacedHelpers,
  } from 'vuex'
  import SnackBarUtil from '@/components/mixins/SnackBarUtil'
  import ErrorDialog from '@/components/generic/ErrorDialog'
  import AnalyteUtil from '@/components/mixins/AnalyteUtil'
  import Subheader from '@/components/generic/Subheader'
  import AppModal from '@/components/generic/AppModal'
  import PageLoader from '@/components/generic/PageLoader'

  const guidelines = createNamespacedHelpers('guidelines')
  const guidelinesparameter = createNamespacedHelpers('guidelinesparameter')
  const cofactor = createNamespacedHelpers('cofactor')
  const analyte = createNamespacedHelpers('analyte')
  const authenticate = createNamespacedHelpers('authenticate')
  const unit = createNamespacedHelpers('unit')

  export default {
    components: {
      PageLoader,
      GuidelinesDetailDefaultValues,
      FormModal,
      AppModal,
      OverrideStrategyConfig,
      StationDataWithFallbackStrategyConfig,
      Subheader,
      ErrorDialog,
    },
    mixins: [SnackBarUtil, AnalyteUtil],
    props: {
      CoFactorAnalyteMismatchedUnitGroups: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        search: '',
        selectedAlgorithm: '',
        configWindowKey: '',
        formModal: false,
        headers: [{
          text: 'Cofactor',
          value: 'CoFactorName',
          align: 'left',
        },
        {
          text: 'Analyte',
          value: 'AnalyteName',
          align: 'left',
        },
        {
          text: 'Description',
          value: 'Description',
          align: 'left',
        },
        {
          text: 'Actions',
          value: null,
          align: 'center',
        },
        ],
        addDialog: false,
        editDialog: false,
        deleteDialog: false,
        targetCofactorName: '',
        cofactorData: {
          Id: null,
          cofactorId: null,
          AnalyteId: null,
        },
        deleteId: null,
        rules: {
          required: (v) => !!v || 'Required.',
        },
        forms: {
          addCofactorFormValid: false,
          editCofactorFormValid: false,
        },
        confirmMessage: '',
      }
    },
    computed: {
      ...analyte.mapGetters(['tenantAnalyteList']),
      ...guidelinesparameter.mapState(['guidelineConfig']),
      ...authenticate.mapGetters(['currentUserGroup']),
      ...cofactor.mapGetters(['cofactorList', 'strategyList', 'cofactorLinkAnalyte']),
      ...unit.mapGetters(['allUnits']),
      canAddCofactors() {
        return this.addCoFactorList && this.addCoFactorList.length > 0
      },
      algorithmIsModified() {
        return this.currentAlgorithm != this.selectedAlgorithm
      },
      strategyOptions() {
        return this.algorithms.map(a => {
          return {
            text: a.Name,
            value: a.Key,
          }
        })
      },
      selectedAlgorithmDescription() {
        let that = this
        let selectedAlgorithm = this.algorithms.find(
          a => a.Key == that.selectedAlgorithm
        )
        if (selectedAlgorithm && selectedAlgorithm.Description) {
          return selectedAlgorithm.Description
        }
        return ''
      },
      isLoading() {
        return this.guidelineConfig.isLoading
      },
      currentAlgorithm() {
        return this.guidelineConfig.data.DataFetchStrategy || ''
      },
      currentAlgorithmName() {
        let strategy = this.strategyOptions.find(
          x => x.value == this.currentAlgorithm
        )
        return strategy && strategy.text ? strategy.text : ''
      },
      noDefaultValue() {
        let strategyText = this.currentAlgorithmName
        return strategyText && strategyText == 'No default value for cofactor' ? true : false
      },
      algorithms() {
        let keys = Object.keys(algorithms)
        return keys.map(key => algorithms[key])
      },
      addCoFactorList() {
        if (this.cofactorList) {
          let newCoFactorList = JSON.parse(JSON.stringify(this.cofactorList))
          this.cofactorLinkAnalyte.forEach(x => {
            let target = newCoFactorList.findIndex(y => y.Id == x.CoFactorId)
            if (target >= 0) {
              newCoFactorList.splice(target, 1)
            }
          })
          return newCoFactorList
        }
      },
      errorDialog() {
        return this.$store.getters['app/errorDialog']
      },
      analyteListMatchCofactorUnit() {
        if (this.cofactorData.cofactorId == null) { return [] }
        let matchCofactor = this.cofactorList.find(x => x.Id == this.cofactorData.cofactorId)
        let matchUnitGroupNames = matchCofactor.UnitGroups.map(x => x.Name)
        let matchUnits = this.allUnits.filter(x => x.Groups.some(y => matchUnitGroupNames.includes(y.Name)))
        let matchAnalytes = this.tenantAnalyteList.filter(x => matchUnits.some(y => y.Id == x.DefaultUnitId))
        return matchAnalytes
      },
      cofactorDataName() {
        let matchCofactor = this.cofactorList.find(x => x.Id == this.cofactorData.cofactorId)
        return matchCofactor.Name
      },
      currentStrategyDefaultValues() {
        let currentDefaultValues = this.strategyList.find(x => x.StrategyCode == this.guidelineConfig.data.DataFetchStrategy)
        return currentDefaultValues ? currentDefaultValues.CoFactorDefaultValues : []
      },
      isCofactorAnalyteMapValid() {
        if (!this.cofactorData.AnalyteId) {
          return true
        }
        return this.validCofactorAnalyteLink(this.cofactorData)
      },
      linkAnalytes() {
        return this.cofactorAnalyteList.filter(x => x.AnalyteId).map(y => y.AnalyteName)
      },
      mapSameAnalyte() {
        if (this.linkAnalytes.length) {
          let mapSameAnalyte = []
          let sameAnalyte = []
          for (let i = 0; i < this.linkAnalytes.length; i++) {
            let condition = sameAnalyte.indexOf(this.linkAnalytes[i]) == -1 && this.linkAnalytes.indexOf(this.linkAnalytes[i]) !== this.linkAnalytes.lastIndexOf(this.linkAnalytes[i])
            if (condition) {
              sameAnalyte.push(this.linkAnalytes[i])
            }
          }
          sameAnalyte.forEach(x => {
            let arr = []
            this.cofactorAnalyteList.forEach(y => {
              if (x == y.AnalyteName) {
                arr.push(y)
              }
            })
            mapSameAnalyte.push(arr)
          })
          return mapSameAnalyte
        } else {
          return []
        }
      },
      cofactorAnalyteList() {
        return this.cofactorLinkAnalyte.map(x => {
          let CoFactor = this.cofactorList.find(a => a.Id == x.CoFactorId)
          let Analyte = this.tenantAnalyteList.find(a => a.Id == x.AnalyteId)
          let Mismatched = this.CoFactorAnalyteMismatchedUnitGroups.find(a => a.Id == x.CoFactorId)
          let temp = {
            ...Mismatched,
            CofactorUnitGroup: CoFactor.UnitGroups.map(x => x.Name).join(','),
          }
          return {
            Id: x.Id,
            CoFactorId: x.CoFactorId,
            CoFactorName: CoFactor.Name,
            AnalyteId: x.AnalyteId,
            AnalyteName: Analyte && Analyte.AnalyteName,
            Description: CoFactor.Description,
            IsMismatched: !!Mismatched,
            Mismatched: temp,
          }
        })
      },
    },
    watch: {
    },
    methods: {
      ...guidelines.mapActions(['putGuidelineConfig']),
      ...guidelinesparameter.mapActions(['getGuidelineConfig']),
      ...cofactor.mapActions(['addOrEditCofactorAnalyteLink', 'getCofactorData', 'deleteCoFactorAnalyteLink']),
      resetFormValues() {
        this.selectedAlgorithm = this.currentAlgorithm
        this.configWindowKey = this.currentAlgorithm
        this.formModal = false
      },
      clickSaveAlgorithmStrategy() {
        this.saveGuidelineConfig()
      },

      // Wait clear requirements to edit these code,Comment them out temporarily

      fetchGuidelineConfig(forced) {
        let that = this
        let options = {
          forced: forced || false,
          successCb() {
            that.resetFormValues()
          },
          failedCb: (msg) => {
            console.error(msg)
            this.$store.dispatch('app/setErrorDialog', {
              data: `failed to fetch tenant guideline configuration: ${msg}`,
              show: true,
            })
          },
        }
        this.getGuidelineConfig(options)
      },
      saveGuidelineConfig() {
        let that = this
        this.getCofactorData(this.currentUserGroup.id)
        that.resetFormValues()
      },
      updateFormModal(dialog) {
        this.formModal = dialog
      },
      emptyCofactorData() {
        this.cofactorData = {
          cofactorId: null,
          AnalyteId: null,
        }
      },
      addCofactor() {
        this.addDialog = true
      },
      clickEdit(data) {
        this.editDialog = true
        this.emptyCofactorData()
        this.cofactorData.Id = data.Id
        this.cofactorData.cofactorId = data.CoFactorId
        this.cofactorData.AnalyteId = data.AnalyteId
      },
      clickDelete(data) {
        this.deleteDialog = true
        this.cofactorData.Id = data.Id
        this.cofactorData.cofactorId = data.CoFactorId
        this.cofactorData.AnalyteId = data.AnalyteId
      },
      closeDialog() {
        this.addDialog = false
        this.editDialog = false
        this.emptyCofactorData()
      },
      saveCofactor() {
        let options = {
          data: {
            CofactorId: this.cofactorData.cofactorId,
            AnalyteId: this.cofactorData.AnalyteId,
            TenantId: this.currentUserGroup.id,
          },
          successCb: () => {
            this.handleNotification({
              type: 'success',
              message: this.$t('GuidelineCoFactor.UpdateCofactorAnalyteLinkSuccessMsg', { cofactorName: this.cofactorDataName }),
            })
            this.emptyCofactorData()
            this.closeDialog()
          },
          failedCb: (err) => {
            this.$store.dispatch('app/setErrorDialog', {
              data: err.response.data.Message,
              show: true,
            })
          },
        }
        this.addOrEditCofactorAnalyteLink(options)
      },
      clickEditSave() {
        let options = {
          data: {
            Id: this.cofactorData.Id,
            CofactorId: this.cofactorData.cofactorId,
            AnalyteId: this.cofactorData.AnalyteId ? this.cofactorData.AnalyteId : null,
            TenantId: this.currentUserGroup.id,
          },
          successCb: () => {
            this.handleNotification({
              type: 'success',
              message: this.$t('GuidelineCoFactor.UpdateCofactorAnalyteLinkSuccessMsg', { cofactorName: this.cofactorDataName }),
            })
            this.emptyCofactorData()
            this.closeDialog()
          },
          failedCb: (err) => {
            this.$store.dispatch('app/setErrorDialog', {
              data: err.response.data.Message,
              show: true,
            })
          },
        }
        this.addOrEditCofactorAnalyteLink(options)
      },
      agreeDelete() {
        let options = {
          Id: this.cofactorData.Id,
          successCb: () => {
            this.handleNotification({
              type: 'success',
              message: this.$t('GuidelineCoFactor.DeleteCofactorAnalyteLinkSuccessMsg', { cofactorName: this.cofactorDataName }),
            })
            this.emptyCofactorData()
            this.deleteDialog = false
          },
          failedCb: (err) => {
            this.$store.dispatch('app/setErrorDialog', {
              data: err.response.data.Message,
              show: true,
            })
          },
        }
        this.deleteCoFactorAnalyteLink(options)
      },
      getHint(cofactorId) {
        var matchedCofactor = this.cofactorList.find(x => x.Id == cofactorId)
        if (matchedCofactor != null) {
          var groupNames = matchedCofactor.UnitGroups.map(x => x.Name)
          return groupNames
        }
        return ''
      },
      validCofactorAnalyteLink(cofactorAnalyteLink) {
        let influencedCofactorIds = this.cofactorAnalyteList.filter(x => !!x.AnalyteId && x.AnalyteId == cofactorAnalyteLink.AnalyteId).map(x => x.CoFactorId)
        if (!influencedCofactorIds.includes(cofactorAnalyteLink.cofactorId)) {
          influencedCofactorIds.push(cofactorAnalyteLink.cofactorId)
        }
        let influencedDefaultValues = this.currentStrategyDefaultValues.filter(x => influencedCofactorIds.includes(x.CoFactorId))
        if (influencedDefaultValues.length == 0) {
          return true
        }
        let unit = influencedDefaultValues[0].Unit
        let defaultValue = influencedDefaultValues[0].DefaultValue
        if (influencedDefaultValues.every(x => x.Unit == unit && x.DefaultValue == defaultValue)) {
          return true
        } else {
          let misMatchCofactorNames = influencedDefaultValues.map(x => x.CoFactorName).join(', ') 
          this.confirmMessage = this.$t('GuidelineCoFactor.ConfirmMsg', { misMatchCofactorNames: misMatchCofactorNames })
          return false
        }
      },
    },
    mounted() {
      this.resetFormValues()
      this.fetchGuidelineConfig()
    },
  }
</script>

<style scoped lang="stylus"></style>